/**
 * The text-message half of "can this plan be created": which steps the SERVER
 * says would run past the 4-text limit once it wraps them (see
 * lib/smsEstimate.ts, and useSmsEstimates for the call), so the host hears it
 * BEFORE Review, in the same list as every other thing standing between the
 * host and Create.
 *
 * Only a server answer holds Review. The local count that stands in while the
 * server is asked is pessimistic and labelled "about"; blocking on it would
 * refuse plans the server accepts. Whatever slips past still meets the server's
 * own validation when the plan is created.
 *
 * Separate from `planIssues` on purpose: that function is about the plan's own
 * fields and needs nothing from the trip.
 */
import { SMS, normalizeChannels } from './broadcastChannels';
import { SMS_SEGMENT_LIMIT, type SmsEstimate } from './smsEstimate';
import { stepDisplayName, type PlanStepDraft } from './reminderPlan';
import { toSmsSafe } from './smsSafeText';

export interface PlanSmsTrip {
  title: string;
  slug: string;
  location?: string | null;
  /** "Give each guest their own link". */
  personalize: boolean;
}

/** One step's text as it will be SENT: folded the way `buildPlanRequests` folds it. */
export const planStepSmsMessage = (draft: Pick<PlanStepDraft, 'smsBody'>): string => toSmsSafe(draft.smsBody).text;

/** One sentence per enabled step the server says is over the limit. Empty when SMS is off. */
export function smsLimitIssues(
  drafts: readonly PlanStepDraft[],
  channels: readonly string[],
  estimateFor: (stepKey: string) => SmsEstimate | null,
): string[] {
  if (!normalizeChannels(channels).includes(SMS)) return [];
  const issues: string[] = [];
  for (const draft of drafts) {
    if (!draft.enabled || !draft.smsBody.trim()) continue;
    const estimate = estimateFor(draft.key);
    if (estimate && estimate.source === 'server' && estimate.overLimit) {
      issues.push(
        estimate.segments <= (estimate.maxSegments ?? SMS_SEGMENT_LIMIT)
          ? `${stepDisplayName(draft.key)}: the text message is longer than the ${estimate.maxBodyLength ?? 280} characters allowed.`
          : `${stepDisplayName(draft.key)}: with the trip name and link added, the text message is ${estimate.segments} texts per guest; the limit is ${estimate.maxSegments ?? SMS_SEGMENT_LIMIT}.`,
      );
    }
  }
  return issues;
}
